/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { MessageStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NotificationRecipientProps {
  id: string;
  name: string | null;
  notificationAddress: string | null;
  userId: string | null;
  messageId: string | null;
  messageStatus: MessageStatus | null;
  notificationId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NotificationRecipientRecord extends Record<NotificationRecipientProps>({
  id: null as any,
  name: null as any,
  notificationAddress: null as any,
  userId: null as any,
  messageId: null as any,
  messageStatus: null as any,
  notificationId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string | null;
    notificationAddress: string | null;
    userId: string | null;
    messageId: string | null;
    messageStatus: MessageStatus | null;
    notificationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NotificationRecipientRecord {
    if (json == null) {
      throw new Error('NotificationRecipientRecord.fromJS: parameter cannot be null');
    }
    return new NotificationRecipientRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NotificationRecipientJson.id',
      ),
      name: json.name,
      notificationAddress: json.notificationAddress,
      userId: json.userId,
      messageId: json.messageId,
      messageStatus: json.messageStatus,
      notificationId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'notificationId', '00000000-0000-0000-0000-000000000000'),
        'NotificationRecipientJson.notificationId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'NotificationRecipientJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string | null;
    notificationAddress: string | null;
    userId: string | null;
    messageId: string | null;
    messageStatus: MessageStatus | null;
    notificationId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NotificationRecipientRecord, Error[]] {
    if (json == null) {
      throw new Error('NotificationRecipientRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NotificationRecipientRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NotificationRecipientJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => json.name,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationAddress: tryOrDefault(
        () => json.notificationAddress,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      userId: tryOrDefault(
        () => json.userId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      messageId: tryOrDefault(
        () => json.messageId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      messageStatus: tryOrDefault(
        () => json.messageStatus,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notificationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'notificationId', '00000000-0000-0000-0000-000000000000'),
            'NotificationRecipientJson.notificationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'NotificationRecipientJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NotificationRecipientProps) {
    super(props);
    if (props == null) {
      throw new Error('NotificationRecipientRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NotificationRecipientViewModelBase implements NotificationRecipientProps {
  protected readonly _props: NotificationRecipientProps;
  private _json?: NotificationRecipientProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string | null {
    return this._props.name;
  }
  get notificationAddress(): string | null {
    return this._props.notificationAddress;
  }
  get userId(): string | null {
    return this._props.userId;
  }
  get messageId(): string | null {
    return this._props.messageId;
  }
  get messageStatus(): MessageStatus | null {
    return this._props.messageStatus;
  }
  get notificationId(): string {
    return this._props.notificationId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCreated() {
    return this._props.messageStatus === MessageStatus.Created;
  }
  public get isFailed() {
    return this._props.messageStatus === MessageStatus.Failed;
  }
  public get isReceivedByRecipient() {
    return this._props.messageStatus === MessageStatus.ReceivedByRecipient;
  }
  public get isSentToProvider() {
    return this._props.messageStatus === MessageStatus.SentToProvider;
  }
  public get isSentToRecipient() {
    return this._props.messageStatus === MessageStatus.SentToRecipient;
  }

  public isAnyMessageStatus(...messageStatus: MessageStatus[]) {
    return this._props.messageStatus != null && messageStatus.includes(this._props.messageStatus);
  }

  constructor(props: NotificationRecipientProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        notificationAddress: this._props.notificationAddress,
        userId: this._props.userId,
        messageId: this._props.messageId,
        messageStatus: this._props.messageStatus,
        notificationId: this._props.notificationId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNotificationRecipientEntities = (state: AppState) =>
  state.entityStores.notificationRecipient.entities;

export const selectNotificationRecipientById = (id: string) => (state: AppState) =>
  state.entityStores.notificationRecipient.entities.get(id);

export const selectNotificationRecipientMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.notificationRecipient.meta.get(id);

export const selectNotificationRecipientAndMetadataById = (id: string) =>
  createSelector(
    selectNotificationRecipientById(id),
    selectNotificationRecipientMetadataById(id),
    (notificationRecipient, meta) => ({ notificationRecipient, meta }),
  );

export function selectNotificationRecipientEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNotificationRecipientEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NotificationRecipientRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNotificationRecipientEntitiesByName(name: string) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNotificationRecipientEntitiesByNotificationAddress(
  notificationAddress: string,
) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.notificationAddress === notificationAddress).valueSeq(),
  );
}

export function selectNotificationRecipientEntitiesByUserId(userId: string) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.userId === userId).valueSeq(),
  );
}

export function selectNotificationRecipientEntitiesByMessageId(messageId: string) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.messageId === messageId).valueSeq(),
  );
}

export function selectNotificationRecipientEntitiesByMessageStatus(messageStatus: MessageStatus) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.messageStatus === messageStatus).valueSeq(),
  );
}

export function selectNotificationRecipientEntitiesByNotificationId(notificationId: string) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.notificationId === notificationId).valueSeq(),
  );
}

export function selectNotificationRecipientEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllNotificationRecipientEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
